"use client";

import Image from "next/image";
import React, { Children, ReactNode, useRef, useState } from "react";

const NavArrow = ({ dir, community }: { dir: "left" | "right"; community?: boolean }) =>
  community ? (
    <Image
      src="/community/mentors/mentor-chevron.svg"
      alt=""
      width={24}
      height={24}
      className={`h-[3rem] w-[3rem] ${dir === "left" ? "rotate-180" : ""}`}
    />
  ) : (
    <svg
      width="8"
      height="13"
      viewBox="0 0 8 13"
      className={`${dir === "right" ? "rotate-180" : ""}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.39129 12.7795L7.14062 12.0263L1.49262 6.38967L7.14062 0.748L6.39129 0L0.00162506 6.38967L6.39129 12.7795Z"
        fill="black"
      />
    </svg>
  );

// Mobile-only carousel: one slide per child, with an arrow + stepper row below.
export function MobileCarousel({
  children,
  variant = "default",
}: {
  children: ReactNode;
  variant?: "default" | "communityMentors";
}) {
  const slides = Children.toArray(children);
  const count = slides.length;
  const community = variant === "communityMentors";
  const pageCount = Math.max(1, count);
  const [active, setActive] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const drag = useRef({ active: false, startX: 0 });
  const trackRef = useRef<HTMLDivElement>(null);

  // Past ~7 the full-size stepper crowds the row between the arrows.
  const compactDots = pageCount > 7;

  const go = (i: number) => setActive(((i % pageCount) + pageCount) % pageCount);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { active: true, startX: event.clientX };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return;
    setDragOffset(event.clientX - drag.current.startX);
  };

  const endDrag = () => {
    if (!drag.current.active) return;
    drag.current.active = false;

    const threshold = (trackRef.current?.clientWidth ?? 0) * 0.15;
    if (dragOffset <= -threshold) go(active + 1);
    else if (dragOffset >= threshold) go(active - 1);
    setDragOffset(0);
  };

  return (
    <div className={`flex w-full flex-col lg:hidden ${community ? "gap-[3.5rem]" : "gap-[4rem]"}`}>
      <div
        ref={trackRef}
        className="w-full cursor-grab touch-pan-y overflow-hidden select-none active:cursor-grabbing"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(event) => event.preventDefault()}>
        <div
          className={`flex ease-[cubic-bezier(0.65,0,0.35,1)] ${
            dragOffset === 0 ? "transition-transform duration-500" : ""
          } ${community ? "gap-[2.5rem] px-[3rem]" : "gap-[1.5rem]"}`}
          style={{
            transform: community
              ? `translateX(calc(${active} * -42rem + ${dragOffset}px))`
              : `translateX(calc(${active} * -1 * (85% + 1.5rem) + ${dragOffset}px))`,
          }}>
          {slides.map((slide, i) => (
            <div
              key={i}
              className={community ? "w-[39.5rem] shrink-0" : "w-[85%] shrink-0"}>
              {slide}
            </div>
          ))}
        </div>
      </div>

      <div className={`flex items-center justify-between ${community ? "px-[3rem]" : ""}`}>
        <button
          type="button"
          aria-label="Previous"
          onClick={() => go(active - 1)}
          className={`flex items-center justify-center rounded-full border border-white/5 bg-white backdrop-blur-[1rem] transition ${
            community ? "h-[6rem] w-[6rem]" : "h-[5rem] w-[5rem]"
          }`}>
          <NavArrow
            dir="left"
            community={community}
          />
        </button>

        <div
          className={`flex items-center ${
            compactDots ? "h-[0.75rem] gap-[1rem]" : "h-[1rem] gap-[1.5rem]"
          }`}>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-full rounded-[0.25rem] bg-white transition-all duration-300 ${
                i === active
                  ? `opacity-100 ${compactDots ? "w-[3rem]" : "w-[4rem]"}`
                  : `opacity-40 ${compactDots ? "w-[1rem]" : "w-[1.5rem]"}`
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="Next"
          onClick={() => go(active + 1)}
          className={`flex items-center justify-center rounded-full border border-white/5 bg-white backdrop-blur-[1rem] transition ${
            community ? "h-[6rem] w-[6rem]" : "h-[5rem] w-[5rem]"
          }`}>
          <NavArrow
            dir="right"
            community={community}
          />
        </button>
      </div>
    </div>
  );
}
